import { Shield } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";

import type { EffectiveSummary } from "@/modules/dashboard/settings/types/access.types";
import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaStatusPill } from "@/shared/components/data-display/ColocaStatusPill";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaSkeleton } from "@/shared/components/feedback/ColocaSkeleton";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { ColocaAccordion, ColocaAccordionItem } from "@/shared/components/layout/ColocaAccordion";
import { ColocaDrawer } from "@/shared/components/overlays/ColocaDrawer";
import useTranslations from "@/shared/lang/useTranslations";
import { formatDateTime } from "@/shared/utils/dateTimeHelpers";

import { buildUserDiff, UPDATE_FIELDS } from "../hooks/useUserMutations";
import type { UpdateUserPayload } from "../types/create-user.types";
import type { InternalUserResponse, UserAssignedRole } from "../types/users-api.types";
import { isEmailConflict, type NormalizedApiError } from "../utils/apiError";
import { resolveSignInHealth } from "../utils/signInHealth";
import { resolveStatusPresentation } from "../utils/statusPresentation";
import {
  buildUserProfileSchema,
  EMPTY_USER_PROFILE_FORM,
  toUserProfileFormValues,
  type UserProfileFormValues,
} from "../utils/userProfileForm";
import { SetPasswordSection } from "./SetPasswordSection";
import { SignInHealthCell } from "./SignInHealthCell";
import { UserDetailFields } from "./UserDetailFields";

export type UserDetailSection = "data" | "access" | "password" | "activity";

/**
 * Is this key one of the profile form's own inputs?
 *
 * `normalizeUserApiError` is already given `UPDATE_FIELDS` and binds nothing else, but `fieldErrors` is typed
 * `Record<string, string>`, so the guarantee is invisible at the call site. Narrowing with the same tuple
 * states the real union of field names instead of asserting one member of it — and since the guard's output
 * has to satisfy `setError`, the "must stay in step with `UserDetailFields`" rule that tuple documents is now
 * checked by the compiler.
 */
function isUpdateField(field: string): field is (typeof UPDATE_FIELDS)[number] {
  return (UPDATE_FIELDS as readonly string[]).includes(field);
}

export interface UserDetailDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: InternalUserResponse | null;
  isLoading: boolean;
  loadError: string | null;
  /** Which section to expand on open — the row menu can deep-link straight to Access or Password. */
  initialSection: UserDetailSection | null;
  organizationLabel: string;
  roles: readonly UserAssignedRole[];
  summary: EffectiveSummary | undefined;
  rolesUnavailable: boolean;
  canWrite: boolean;
  canManageRoles: boolean;
  isBusy: boolean;
  error: NormalizedApiError | null;
  onSave: (payload: UpdateUserPayload) => void;
  onSetPassword: (password: string) => void;
  onManageAccess: () => void;
}

/**
 * Read, triage and edit one person.
 *
 * **Everything is collapsed on open** and each header carries its own summary, so the operator decides what
 * to expand without expanding it. The sign-in alert sits ABOVE the accordion rather than inside _Actividad_,
 * because the drawer's first job is triage — the answer to "why can't they get in" should not be three clicks
 * away.
 *
 * **Each editable section owns its own form and its own submit button.** That is forced by the accordion: its
 * panels UNMOUNT when collapsed, so a single footer button targeting a form inside a panel would be inert
 * whenever that panel was closed. Two sibling forms also avoid nesting `<form>` elements, which is invalid
 * HTML. The footer therefore carries only the close action and the organization label.
 *
 * The save path builds a **diff**. `UpdateUserPayload` cannot express `user_type` or `organization_id`, which
 * is what makes editing a Coloca staff member possible at all: resending those two is a hard 400.
 */
export function UserDetailDrawer({
  open,
  onOpenChange,
  user,
  isLoading,
  loadError,
  initialSection,
  organizationLabel,
  roles,
  summary,
  rolesUnavailable,
  canWrite,
  canManageRoles,
  isBusy,
  error,
  onSave,
  onSetPassword,
  onManageAccess,
}: UserDetailDrawerProps) {
  const { t } = useTranslations("users");
  const [section, setSection] = useState<UserDetailSection | null>(null);

  const schema = useMemo(
    () =>
      buildUserProfileSchema({
        required: t("validation_required"),
        email: t("validation_email"),
        phone: t("create_field_phone_hint"),
      }),
    [t],
  );

  const form = useZodForm(schema, EMPTY_USER_PROFILE_FORM);
  const { reset, setError } = form;

  /**
   * Which person the form currently holds.
   *
   * Tracked in a ref rather than derived from `open && user`, because `useUserDetail` returns `null` while a
   * refetch is in flight: a derived key would go `id → null → id` and reset the form mid-edit, silently
   * discarding what the operator had typed. Keying on "the id actually changed" leaves a same-person refetch
   * alone, while opening a different person — or reopening the same one after a close — reseeds it.
   *
   * This is only safe because `useUserDetail` treats every open as a NEW request: the first non-null `user`
   * of an open is therefore always freshly fetched. When it served a cached record instead, this guard was
   * what pinned the form to it — it seeded from the stale copy and then blocked the re-seed when the real
   * response arrived. If that hook ever regains a cache, this must key on record identity, not just the id.
   */
  const seededIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!open) {
      seededIdRef.current = null;
      return;
    }
    if (!user || seededIdRef.current === user.id) return;
    seededIdRef.current = user.id;
    reset(toUserProfileFormValues(user));
  }, [open, user, reset]);

  /**
   * Reset the expanded section when the drawer opens, or opens on a different deep-link target.
   *
   * Done during render — React's documented "adjust state when a prop changes" pattern, the same shape
   * `AssignRolesDrawer` uses — because a synchronous `setState` inside an effect is a cascading render.
   *
   * The key deliberately excludes the loaded user: `useUserDetail` returns null mid-refetch, and including it
   * would collapse the panel the operator had just opened.
   */
  const sectionKey = open ? `open:${initialSection ?? ""}` : "closed";
  const [seededSectionKey, setSeededSectionKey] = useState("closed");
  if (sectionKey !== seededSectionKey) {
    setSeededSectionKey(sectionKey);
    setSection(open ? (initialSection ?? null) : null);
  }

  // Server-side messages bind to the inputs that own them, so a duplicate email lands on the email field
  // rather than in a toast the operator has to translate back into an action.
  useEffect(() => {
    if (!open || !error) return;
    for (const [field, message] of Object.entries(error.fieldErrors)) {
      if (!isUpdateField(field)) continue;
      setError(field, {
        type: "server",
        // The API's own 409 sentence is English and quotes the address back — rule 6 says this is translated.
        message: field === "email" && isEmailConflict(error) ? t("error_email_taken") : message,
      });
    }
  }, [open, error, setError, t]);

  const health = useMemo(() => (user ? resolveSignInHealth(user) : null), [user]);
  const statusPill = useMemo(() => (user ? resolveStatusPresentation(user.status, t) : null), [user, t]);

  const alert = (() => {
    if (!health) return null;
    if (health.key === "locked") {
      return {
        variant: "destructive" as const,
        text: t("detail_alert_locked", { minutes: health.lockedMinutes }),
      };
    }
    // The only honest 2FA signal available: the flag is on and the person has never signed in. Phrased as
    // something to check, because `two_factor_secret` is @Exclude()d so from here it is an inference.
    if (user?.two_factor_enabled && user.last_login_at === null) {
      return { variant: "warning" as const, text: t("detail_alert_two_factor") };
    }
    return null;
  })();

  const accessSummaryText = rolesUnavailable
    ? t("detail_access_summary_unavailable")
    : roles.length === 0
      ? t("detail_access_summary_none")
      : // `count` is i18next's reserved plural variable, so a single-role person reads "1 rol asignado"
        // instead of "1 roles asignados".
        t("detail_access_summary", {
          count: roles.length,
          allowed: summary?.allowed ?? 0,
          total: summary?.total ?? 0,
        });

  const submitProfile = (values: UserProfileFormValues) => {
    if (!user) return;
    onSave(buildUserDiff(user, values));
  };

  return (
    <ColocaDrawer
      open={open}
      onOpenChange={onOpenChange}
      size="lg"
      // The body is a single root element, so the drawer would otherwise pin its LAST child to the
      // bottom edge — here the accordion, which is content, not an action row. That pin is what
      // dragged it down a tall panel (mt-auto) and floated it over the scrolling fields on a short one
      // (sticky bottom-0). The actions live in the footer prop, so nothing needs pinning.
      pinLastChild={false}
      title={user ? user.full_name : t("detail_section_data")}
      description={user?.email}
      footer={
        <div className="flex w-full items-center justify-between gap-3">
          <span className="truncate text-xs text-muted-foreground">
            {t("detail_org_label")} · {organizationLabel}
          </span>
          <ColocaButton variant="secondary" size="sm" onClick={() => onOpenChange(false)}>
            {t("detail_close")}
          </ColocaButton>
        </div>
      }
    >
      {isLoading && (
        <div className="space-y-3">
          <ColocaSkeleton className="h-16 w-full" />
          <ColocaSkeleton className="h-12 w-full" />
          <ColocaSkeleton className="h-12 w-full" />
        </div>
      )}

      {!isLoading && loadError && <ColocaAlert variant="destructive" text={loadError} />}

      {!isLoading && user && health && statusPill && (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            {/* Same resolver the table row uses, so the two can never disagree — this used to print the raw
                enum in a hardcoded neutral pill while the row showed a translated danger one. */}
            <ColocaStatusPill variant={statusPill.variant} withDot>
              {statusPill.label}
            </ColocaStatusPill>
            <SignInHealthCell health={health} />
          </div>

          {alert && <ColocaAlert variant={alert.variant} text={alert.text} />}

          {error && Object.keys(error.fieldErrors).length === 0 && error.message && (
            <ColocaAlert variant="destructive" text={error.message} />
          )}

          <ColocaAccordion>
            <ColocaAccordionItem
              title={t("detail_section_data")}
              open={section === "data"}
              onOpenChange={(next) => setSection(next ? "data" : null)}
            >
              <ColocaForm form={form} onSubmit={submitProfile} className="space-y-4">
                <UserDetailFields disabled={!canWrite} />
                {canWrite && (
                  <div className="flex justify-end">
                    <ColocaButton type="submit" variant="primary" size="sm" loading={isBusy}>
                      {t("detail_save")}
                    </ColocaButton>
                  </div>
                )}
              </ColocaForm>
            </ColocaAccordionItem>

            <ColocaAccordionItem
              title={t("detail_section_access")}
              meta={
                <span className="font-geist-mono text-xs">
                  {rolesUnavailable
                    ? "—"
                    : `${roles.length} · ${summary?.allowed ?? 0}/${summary?.total ?? 0}`}
                </span>
              }
              open={section === "access"}
              onOpenChange={(next) => setSection(next ? "access" : null)}
            >
              <div className="flex flex-col gap-3 pt-2">
                <p className="text-sm text-muted-foreground">{accessSummaryText}</p>
                {canManageRoles && (
                  <ColocaButton variant="secondary" size="sm" icon={Shield} onClick={onManageAccess}>
                    {t("detail_manage_access")}
                  </ColocaButton>
                )}
              </div>
            </ColocaAccordionItem>

            {canWrite && (
              <ColocaAccordionItem
                title={t("detail_section_password")}
                open={section === "password"}
                onOpenChange={(next) => setSection(next ? "password" : null)}
              >
                {/* Keyed by person so reopening on someone else can never inherit a typed password. */}
                <SetPasswordSection key={user.id} isBusy={isBusy} onSubmit={onSetPassword} />
              </ColocaAccordionItem>
            )}

            <ColocaAccordionItem
              title={t("detail_section_activity")}
              open={section === "activity"}
              onOpenChange={(next) => setSection(next ? "activity" : null)}
            >
              <ActivityFacts user={user} />
            </ColocaAccordionItem>
          </ColocaAccordion>
        </div>
      )}
    </ColocaDrawer>
  );
}

function ActivityFacts({ user }: { user: InternalUserResponse }) {
  const { t } = useTranslations("users");
  const stamp = (value: string | null) =>
    value ? formatDateTime(value, { includeTime: true }) : t("activity_never");

  const facts: { label: string; value: string }[] = [
    { label: t("activity_created"), value: stamp(user.created_at) },
    { label: t("activity_updated"), value: stamp(user.updated_at) },
    { label: t("activity_last_login"), value: stamp(user.last_login_at) },
    { label: t("activity_password_changed"), value: stamp(user.password_changed_at) },
    { label: t("activity_email_verified"), value: stamp(user.email_verified_at) },
    { label: t("activity_failed_attempts"), value: String(user.failed_login_attempts) },
    { label: t("activity_locked_until"), value: stamp(user.locked_until) },
    // Named "(indicador)" on purpose: the flag alone cannot tell an enrolled device from a lockout.
    {
      label: t("activity_two_factor_flag"),
      value: user.two_factor_enabled ? t("activity_yes") : t("activity_no"),
    },
  ];

  return (
    <div className="flex flex-col pt-1">
      {facts.map((fact) => (
        <div
          key={fact.label}
          className="flex items-center justify-between gap-4 border-b border-border py-2 last:border-b-0"
        >
          <span className="text-xs text-muted-foreground">{fact.label}</span>
          <span className="font-geist-mono text-xs tabular-nums text-foreground">{fact.value}</span>
        </div>
      ))}
    </div>
  );
}
